import { useEffect, useState } from "react";
import SendIcon from "@mui/icons-material/Send";
import { useForm } from "react-hook-form";
import CloseIcon from "@mui/icons-material/Close";

import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";
import { getTranslateByKey, isNullUndefined, removeEmptyValues } from "@/main/services/utils";
import { KeyValue } from "@/main/models/common";
import KogInputTextControlledNew from "@/main/components/shared/inputText/inputTextControlledNew";
import * as S from "./styles";
import { IconButton } from "@mui/material";
import KogInputAutoCompleteCheckControlled from "@/main/components/shared/inputAutoCompleteCheck/inputAutoCompleteCheckControlled";
import { CompanyTypeEnum } from "@/main/services/enum/CompanyTypeEnum";
import { SpinnerLoading } from "@/main/components/shared/styles";
import { quoteItemCommentApi } from "@/main/api";
import { QuoteItemCommentRequest } from "@/main/models/request";
import { toast } from "react-toastify";
import { QuoteItemCommentHeaderResponse, QuoteItemCommentResponse } from "@/main/models/response";
import { useUserReducer } from "@/main/store/user";
import {
	findCompanyTypeByTranslate,
	getOptionByIdSenderCompanyType,
	getWhoSeesByIdSenderCompanyType,
	isSameRecipientCompanyTypeByUser,
} from "./utilsFooterComment";

const INITIAL_FORM_STATE = {
	sendTo: null as KeyValue | null,
	whoSees: [] as KeyValue[],
	message: "",
};

type ICommentForm = typeof INITIAL_FORM_STATE;

interface IFooterCommentProps {
	guidQuoteItem: string;
	messageReply: QuoteItemCommentResponse | undefined;
	dataHeader: QuoteItemCommentHeaderResponse | null;
	loadComments: () => void;
	removeReply: () => void;
}
function FooterComment({ guidQuoteItem, messageReply, dataHeader, loadComments, removeReply }: IFooterCommentProps) {
	const { currentUser } = useUserReducer();
	const ID_SCREEN = "id-modal-search-claim-comment";
	const generateIdScreen = (id: string) => `${ID_SCREEN}-${id}`;
	const { control, handleSubmit, setValue, resetField, watch } = useForm<ICommentForm>({
		defaultValues: INITIAL_FORM_STATE,
	});
	const watcherSendTo = watch("sendTo");

	const [isLoadingSave, setIsLoadingSave] = useState(false);

	function onSubmitMessage(data: ICommentForm) {
		setIsLoadingSave(true);

		const request: QuoteItemCommentRequest = {
			guidReplyComment: isNullUndefined(messageReply) ? "" : messageReply.guidQuoteItemComment,
			guidQuoteItem: guidQuoteItem,
			text: data.message,
			idRecipientCompanyType: isNullUndefined(data.sendTo) ? CompanyTypeEnum.Supervisor : Number(data.sendTo.key),
			commentPermissions: data.whoSees.map((item) => ({
				idCompanyType: Number(item.key),
			})),
		};

		quoteItemCommentApi
			.save(removeEmptyValues(request))
			.then(() => {
				const message = getTranslateByKey("information.commentSentSuccessfully");
				toast.success(message);
				resetField("message");
				loadComments();
				removeReply();
			})
			.finally(() => {
				setIsLoadingSave(false);
			});
	}

	const OPTIONS: KeyValue[] = [
		{
			key: CompanyTypeEnum.Admin,
			value: getTranslateByKey("contactType.administrator"),
		},
		{
			key: CompanyTypeEnum.BodyShop,
			value: getTranslateByKey("information.bodyShop"),
		},
		{
			key: CompanyTypeEnum.Insurer,
			value: getTranslateByKey("information.insurer"),
		},
		{
			key: CompanyTypeEnum.Supplier,
			value: getTranslateByKey("information.supplier"),
		},
	];

	function getOptionsFilter() {
		let filteredOptions = OPTIONS.sort((a, b) => a.value.localeCompare(b.value));

		if (!isNullUndefined(dataHeader) && !dataHeader.hasProcurement) {
			filteredOptions = filteredOptions.filter((item) => item.key !== CompanyTypeEnum.Supplier);
		}

		if (!isNullUndefined(currentUser)) {
			filteredOptions = filteredOptions.filter((item) => item.key !== currentUser.idCompanyType);
		}

		return filteredOptions;
	}

	const filteredOptions = getOptionsFilter();

	function changeWhoSeesBySendTo(optionSendTo: KeyValue | null = null) {
		if (isNullUndefined(optionSendTo)) {
			setValue("whoSees", []);
			return;
		}

		const whoSees = filteredOptions.filter((item) => item.key === optionSendTo.key);
		setValue("whoSees", whoSees);
	}

	function fillDropsWhenReply() {
		if (isNullUndefined(messageReply) || isNullUndefined(currentUser)) return;
		const isSameRecipient = isSameRecipientCompanyTypeByUser(
			messageReply.recipientCompanyType,
			currentUser.idCompanyType,
			OPTIONS,
		);
		const sendTo = isSameRecipient
			? getOptionByIdSenderCompanyType(OPTIONS, messageReply.idSenderCompanyType)
			: findCompanyTypeByTranslate(messageReply.recipientCompanyType, filteredOptions);
		setValue("sendTo", sendTo);

		const whoSeesBySendTo = getWhoSeesByIdSenderCompanyType(messageReply.idSenderCompanyType, filteredOptions);
		const whoSees = messageReply.companyTypePermissions.map((item) =>
			findCompanyTypeByTranslate(item, filteredOptions),
		);
		const whoSeesFiltered = whoSees.filter((item) => !isNullUndefined(item));

		const mergedWhoSees = new Set([...whoSeesBySendTo, ...whoSeesFiltered]);
		const mergedWhoSeesArray = Array.from(mergedWhoSees);
		setValue("whoSees", mergedWhoSeesArray);
	}

	useEffect(() => {
		fillDropsWhenReply();
	}, [JSON.stringify(messageReply)]);

	return (
		<form onSubmit={handleSubmit(onSubmitMessage)} noValidate>
			<S.Container>
				<S.AreaDropdowns>
					<KogAutoCompleteControlled
						id={generateIdScreen("select-sendTo")}
						nameCustom="sendTo"
						control={control}
						label={getTranslateByKey("information.sendTo")}
						rulesCustom={{ required: true }}
						options={filteredOptions}
						isLoading={false}
						onChangeCustom={(value) => {
							changeWhoSeesBySendTo(value);
						}}
					/>
					<KogInputAutoCompleteCheckControlled
						name="whoSees"
						control={control}
						label={getTranslateByKey("information.whoSees")}
						options={filteredOptions}
						keysOptionsDisabled={isNullUndefined(watcherSendTo) ? [] : [watcherSendTo.key]}
						selectAllLabel={getTranslateByKey("information.selectAll")}
						controllerProps={{ rules: { required: true } }}
						customInputProps={{
							required: true,
							id: generateIdScreen("select-insurer"),
						}}
						isLoading={false}
						onChangeCustom={() => {}}
					/>
				</S.AreaDropdowns>
				{!isNullUndefined(messageReply) && (
					<S.AreaMessageReply>
						{messageReply.senderCompany} - {messageReply.dateFormatted} <br />
						{messageReply.text}
						<S.AreaRemoveReply>
							<IconButton onClick={() => removeReply()}>
								<CloseIcon fontSize="small" />
							</IconButton>
						</S.AreaRemoveReply>
					</S.AreaMessageReply>
				)}

				<S.AreaTextArea>
					<KogInputTextControlledNew
						id={generateIdScreen("input-message")}
						label={getTranslateByKey("information.message")}
						placeholder={getTranslateByKey("information.typeMessage")}
						name="message"
						control={control}
						controllerProps={{ rules: { required: true } }}
						inputProps={{}}
						multiline
						minRows={4}
						maxRows={10}
					/>
					{isLoadingSave ? (
						<SpinnerLoading />
					) : (
						<IconButton disabled={isLoadingSave} type="submit" id="id-searchClaim-btn-send-message">
							<SendIcon />
						</IconButton>
					)}
				</S.AreaTextArea>
			</S.Container>
		</form>
	);
}

export default FooterComment;
